/**
 * Cross-renderer uPlot primitives: strictly-increasing-x
 * dedupe, gap detection/injection, axis builders, threshold collection, axis
 * bounds, UTC tick handling, and the destroy-and-rebuild step. Pure functions
 * plus `rebuildUplot` (the one that touches the uPlot instance). The renderers
 * (`render-line`/`render-scatter`/`render-histogram`) compose these.
 */

import uPlot from 'uplot';
import { runtime } from '../plot-runtime.ts';
import { plotState, FONT_SIZE_DEFAULT, type AxisBounds, type AxisTitle } from '../../../state/plot-state.ts';
import { getFilters } from '../../../state/request.ts';
import { chartReady } from '../plot-ui-state.ts';
import { attachLegendTools } from './legend-tools.ts';
import { chartContainerSize, type ThemeColors } from './chart-dom.ts';
import { hideTooltip } from './tooltip.ts';
import { drawBranding } from './overlays.ts';
import { precisionZoomPlugin } from './precision-zoom.ts';
import { FOCUS_DIM_ALPHA } from '../plot-constants.ts';
import type { PlotResponse } from '../../../api/types.ts';

type NumArr = Array<number | null>;

/**
 * uPlot renders time-axis ticks via `tzDate(ts)` read with local accessors
 * (getHours…). Shift the Date by the browser tz offset so those local reads
 * render UTC - cheaper than depending on Intl/uPlot.tzDate, and keeps ticks,
 * tooltips, pickers and stored data all on UTC.
 */
export function utcTzDate(ts: number): Date {
  const d = new Date(ts * 1000);
  return new Date(d.getTime() + d.getTimezoneOffset() * 60000);
}

/** Pointer distance, in px, within which uPlot calls a series "hovered". */
const FOCUS_PROX = 20;

/**
 * Apply hover focus - dim every series but the nearest one - if the user asked
 * for it in chart settings.
 *
 * Off by default. uPlot's focus fades the whole chart to 15% around whatever
 * the pointer is closest to, which reads as a highlight on a three-line chart
 * and as a bug on a scatter cloud or a six-ship overlay. Both halves have to
 * agree: `cursor.focus` decides what counts as hovered, `focus.alpha` decides
 * what the rest look like.
 */
export function applyHoverFocus(opts: uPlot.Options, enabled?: boolean): void {
  if (!(enabled ?? plotState.value.style.focus_hover)) return;
  opts.cursor = { ...opts.cursor, focus: { prox: FOCUS_PROX } };
  opts.focus = { alpha: FOCUS_DIM_ALPHA };
}

/** Collapse duplicate x (keep last) - uPlot requires strictly increasing x. */
export function dedupeByX(
  xs: number[],
  seriesValues: NumArr[],
): { x: number[]; series: NumArr[] } {
  if (xs.length < 2) {
    return { x: xs.slice(), series: seriesValues.map((a) => a.slice()) };
  }
  const outX = [xs[0]!];
  const outS = seriesValues.map((a) => [a[0]!]);
  for (let i = 1; i < xs.length; i++) {
    if (xs[i] === xs[i - 1]) {
      for (let k = 0; k < seriesValues.length; k++) {
        outS[k]![outS[k]!.length - 1] = seriesValues[k]![i]!;
      }
    } else {
      outX.push(xs[i]!);
      for (let k = 0; k < seriesValues.length; k++) outS[k]!.push(seriesValues[k]![i]!);
    }
  }
  return { x: outX, series: outS };
}

/** Gap threshold = 5× the median positive delta (blob heuristic). */
export function computeGapThreshold(xData: number[]): number | null {
  if (!xData || xData.length < 3) return null;
  const deltas: number[] = [];
  for (let k = 1; k < xData.length; k++) {
    const d = xData[k]! - xData[k - 1]!;
    if (Number.isFinite(d) && d > 0) deltas.push(d);
  }
  if (deltas.length < 2) return null;
  deltas.sort((a, b) => a - b);
  const mid = deltas[Math.floor(deltas.length / 2)]!;
  return mid * 5;
}

/** Insert `[x, null]` samples across big gaps so uPlot breaks the line. */
export function injectGaps(
  xs: number[],
  seriesValues: NumArr[],
  gapThreshold: number | null,
): { x: number[]; series: NumArr[] } {
  if (gapThreshold == null) return { x: xs, series: seriesValues };
  const outX: number[] = [];
  const outS: NumArr[] = seriesValues.map(() => []);
  for (let i = 0; i < xs.length; i++) {
    outX.push(xs[i]!);
    for (let k = 0; k < seriesValues.length; k++) outS[k]!.push(seriesValues[k]![i]!);
    if (i < xs.length - 1 && xs[i + 1]! - xs[i]! > gapThreshold) {
      const mid = xs[i]! + (xs[i + 1]! - xs[i]!) / 2;
      outX.push(mid);
      for (let k = 0; k < seriesValues.length; k++) outS[k]!.push(null);
    }
  }
  return { x: outX, series: outS };
}

/**
 * Let each line join its own points across rows it has no value in, and break
 * only where its own points are far apart.
 *
 * Live data merges tables that write at different rates (engine every 30 s,
 * nautical every 8 s) onto one time axis. A 30 s signal is then NULL on every
 * nautical row between its own points, and uPlot breaks a line at each NULL,
 * so it draws no line at all. Here each series is judged against its own
 * points only: a run of NULLs between two of them closer than 5x that series'
 * median step becomes `undefined`, which uPlot draws through; a longer one
 * stays NULL, a real gap. No value is invented - the line only joins points
 * that are there.
 */
export function bridgeOwnGaps(xs: number[], series: NumArr[]): NumArr[] {
  return series.map((ys) => {
    const own: number[] = [];
    for (let i = 0; i < ys.length; i++) if (ys[i] != null) own.push(xs[i]!);
    const threshold = computeGapThreshold(own) ?? Infinity;
    const out: Array<number | null | undefined> = ys.slice();
    let prev = -1;
    for (let i = 0; i < out.length; i++) {
      if (out[i] == null) continue;
      if (prev >= 0 && i - prev > 1 && xs[i]! - xs[prev]! <= threshold) {
        for (let k = prev + 1; k < i; k++) out[k] = undefined;
      }
      prev = i;
    }
    // uPlot reads `undefined` as "no point here, keep the line going".
    return out as NumArr;
  });
}

/**
 * uPlot legend only when ≥2 series, and only when it is wanted; cursor-synced
 * live values stay off.
 *
 * `show` defaults to the Explore chart's setting. A dashboard tile passes its
 * own, because a tile carries the style it was saved with rather than whatever
 * Explore is set to right now.
 */
export function buildLegendOpts(
  seriesCount: number,
  show: boolean = plotState.value.style.show_legend,
): { show: boolean; live?: boolean } {
  if (seriesCount < 2 || !show) return { show: false };
  return { show: true, live: false };
}

export function joinLabels(names: string[], max = 3): string {
  const uniq = [...new Set(names.filter(Boolean))];
  if (!uniq.length) return '';
  if (uniq.length <= max) return uniq.join(', ');
  return uniq.slice(0, max).join(', ') + ` (+${uniq.length - max})`;
}

/**
 * The title of one Y axis, as `title` asks. Series are named by their column,
 * so a multi-ship overlay says "SOG" once rather than once per ship. Picked
 * signals that are no longer on this axis drop out; with none left it is auto.
 */
export function yAxisTitle(
  series: ReadonlyArray<{ name: string; axis: 'left' | 'right'; column?: string }>,
  side: 'left' | 'right',
  title?: AxisTitle,
): string {
  const names = series.filter((s) => (s.axis === 'right') === (side === 'right')).map((s) => s.column ?? s.name);
  if (title?.mode === 'off') return '';
  if (title?.mode === 'text' && title.text.trim()) return title.text.trim();
  if (title?.mode === 'signals') {
    const picked = [...new Set(names)].filter((n) => title.signals.includes(n));
    if (picked.length) return picked.join(', ');
  }
  return joinLabels(names);
}

export function formatXTick(v: number): string {
  if (!Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  if (abs >= 1000 || (abs > 0 && abs < 0.01)) return v.toPrecision(4);
  return Number(v.toFixed(3)).toString();
}

/** The chart's text size - the shared setting unless a caller carries its own
 *  (a dashboard tile renders from its spec, not from what Explore is showing). */
export function chartFontPx(fontPx?: number): number {
  return fontPx ?? plotState.value.style.font_size ?? FONT_SIZE_DEFAULT;
}

/** Axis-value font at the chart's text size. */
export function axisValueFont(fontPx: number): string {
  return `${fontPx}px system-ui, -apple-system, sans-serif`;
}

/**
 * Publish the chart's text size to CSS as `--chart-font-size`, for the parts
 * drawn as DOM rather than on the canvas (the legend, the tooltip).
 */
export function applyChartFontVar(el: HTMLElement, fontPx?: number): void {
  el.style.setProperty('--chart-font-size', `${chartFontPx(fontPx)}px`);
}

/** Axis-label font: a shade smaller than the values it heads, and bolder. */
export function axisLabelFont(fontPx: number): string {
  return `600 ${Math.max(9, fontPx - 1)}px system-ui, -apple-system, sans-serif`;
}

export function buildXAxis(
  c: ThemeColors,
  isTime: boolean,
  label: string,
  fontPx?: number,
): uPlot.Axis {
  const size = chartFontPx(fontPx);
  const ax: uPlot.Axis = {
    scale: 'x',
    stroke: c.text,
    grid: { stroke: c.grid, width: 1 },
    ticks: { stroke: c.grid, width: 1 },
    space: 80,
    font: axisValueFont(size),
  };
  if (!isTime) {
    ax.values = (_u, splits) => splits.map((v) => formatXTick(v));
  }
  if (label) {
    ax.label = label;
    ax.labelFont = axisLabelFont(size);
    ax.labelGap = 2;
    // The gutter follows the text: at 20px the stock 22 clipped the label it
    // was reserved for.
    ax.labelSize = 22 + Math.max(0, size - FONT_SIZE_DEFAULT) * 1.4;
  }
  return ax;
}

export function buildYAxis(
  c: ThemeColors,
  scaleKey: string,
  side: 'left' | 'right',
  label: string,
  fontPx?: number,
): uPlot.Axis {
  const size = chartFontPx(fontPx);
  const ax: uPlot.Axis = {
    scale: scaleKey,
    side: side === 'right' ? 1 : 3,
    stroke: c.text,
    grid: { stroke: side === 'right' ? 'transparent' : c.grid, width: 1 },
    ticks: { stroke: c.grid, width: 1 },
    // Wide enough for the numbers at this size - a fixed 60 truncated
    // "1.100e+4" the moment the text grew.
    size: Math.round(60 * (size / FONT_SIZE_DEFAULT)),
    font: axisValueFont(size),
  };
  if (label) {
    ax.label = label;
    ax.labelFont = axisLabelFont(size);
    ax.labelGap = 2;
    ax.labelSize = 18 + Math.max(0, size - FONT_SIZE_DEFAULT) * 1.4;
  }
  return ax;
}

export interface Threshold {
  value: number;
  scale: string;
  label: string;
}

/** Horizontal threshold lines from numeric filters on plotted series. */
export function collectThresholds(data: PlotResponse): Threshold[] {
  const filters = getFilters();
  if (!filters || !filters.conditions.length) return [];
  const hasRight = data.series.some((s) => s.axis === 'right');
  const out: Threshold[] = [];
  for (const f of filters.conditions) {
    const match = data.series.find((s) => s.name === f.column);
    if (!match) continue;
    const v = Number(f.value);
    if (!Number.isFinite(v)) continue;
    out.push({
      value: v,
      scale: match.axis === 'right' && hasRight ? 'yR' : 'y',
      label: `${f.column} ${f.op} ${f.value}`,
    });
  }
  return out;
}

/** Apply user axis-bound overrides onto a uPlot `scales` object. */
export function applyAxisBounds(
  scales: Record<string, uPlot.Scale>,
  xIsTime: boolean,
  hasRight: boolean,
  bounds?: AxisBounds,
): void {
  const ab = bounds ?? plotState.value.axis_bounds;
  const makeRange =
    (lo: number | null, hi: number | null): uPlot.Scale.Range =>
    (_u, dMin, dMax) =>
      [lo != null ? lo : dMin, hi != null ? hi : dMax];
  if (!xIsTime && (ab.x_min != null || ab.x_max != null)) {
    scales.x = scales.x || {};
    scales.x.range = makeRange(ab.x_min, ab.x_max);
  }
  if (ab.y_min != null || ab.y_max != null) {
    scales.y = scales.y || {};
    scales.y.range = makeRange(ab.y_min, ab.y_max);
  }
  if (hasRight && (ab.yr_min != null || ab.yr_max != null)) {
    scales.yR = scales.yR || {};
    scales.yR.range = makeRange(ab.yr_min, ab.yr_max);
  }
}

/**
 * A live chart that follows the clock is built on the clock's window, not the
 * data's: `runtime.dataRange` is that window while it follows (`live-tick.ts`).
 * Without this a rebuild - any ⚙ change - starts on the data's own extent, the
 * next resize reads that as a zoom, and the chart stops following. Double-click
 * goes back to the same window, as Reset zoom does, instead of uPlot's own
 * "fit the data".
 */
export function followLiveWindow(opts: uPlot.Options): void {
  const following = (): boolean => runtime.liveNowS != null && !!runtime.dataRange;
  opts.cursor = opts.cursor ?? {};
  opts.cursor.bind = {
    ...opts.cursor.bind,
    dblclick: (u, _targ, handler) => (e) => {
      if (following()) u.setScale('x', { ...runtime.dataRange! });
      else handler(e);
      return null;
    },
  };
}

/** Put a new instance on the followed window before its first paint. */
export function pinLiveWindow(u: uPlot): void {
  if (runtime.liveNowS != null && runtime.dataRange) u.setScale('x', { ...runtime.dataRange });
}

/** Destroy any current uPlot and mount a fresh one into the chart root. */
export function rebuildUplot(uData: uPlot.AlignedData, opts: uPlot.Options): void {
  if (runtime.instance) {
    runtime.instance.destroy();
    runtime.instance = null;
  }
  const root = runtime.rootEl;
  if (!root) return;
  hideTooltip();
  // The DOM text around the canvas - uPlot's legend, the tooltip - follows the
  // same setting as the text drawn on it. Set as a variable rather than styled
  // per node so the CSS keeps owning everything but the size.
  applyChartFontVar(root);
  if (runtime.containerEl) applyChartFontVar(runtime.containerEl);
  // Append the branding watermark to whatever draw hooks the renderer set, so
  // every plot type carries it (on the canvas → it also lands in exports).
  opts.hooks = opts.hooks || {};
  const existingDraw = opts.hooks.draw;
  const drawHooks =
    existingDraw == null ? [] : Array.isArray(existingDraw) ? existingDraw.slice() : [existingDraw];
  drawHooks.push(drawBranding);
  opts.hooks.draw = drawHooks;
  // Precision zoom rides on the instance as a plugin, so it comes and goes with it.
  if (plotState.value.style.precision_zoom && (runtime.lastData?.plot_type === 'line' || runtime.lastData?.plot_type === 'scatter')) {
    opts.plugins = [...(opts.plugins ?? []), precisionZoomPlugin()];
  }
  followLiveWindow(opts);
  runtime.inRebuild = true;
  try {
    runtime.instance = new uPlot(opts, uData, root);
    pinLiveWindow(runtime.instance);
  } finally {
    runtime.inRebuild = false;
  }
  // Before the measurement below, so the legend's height already includes the
  // bulk-control row when the canvas is sized against it.
  attachLegendTools(runtime.instance);
  chartReady.set(true);
  // uPlot builds the legend synchronously, but the first size (opts.width/height)
  // was measured before it existed, so the canvas claimed the legend's height
  // too. Re-measure now - before the browser paints - so the corrected size is
  // the first thing rendered (no one-frame overflow into the clipped card).
  const legendEl0 = root.querySelector<HTMLElement>('.u-legend');
  if (legendEl0 && legendEl0.offsetParent !== null) {
    runtime.instance.setSize(chartContainerSize());
  }
  // Belt-and-suspenders: re-measure after layout settles (fonts, late reflow).
  requestAnimationFrame(() => {
    if (!runtime.instance) return;
    const legendEl = root.querySelector<HTMLElement>('.u-legend');
    if (!legendEl || legendEl.offsetParent === null) return;
    runtime.instance.setSize(chartContainerSize());
  });
}
